import type {
  Patient,
  OpportunityKind,
  OpportunityDef,
  PatientSignal,
  Status,
} from './types';

// Anchored to the current mock "today" (Wed 6 May 2026 in Today.tsx).
// When real data lands, replace with `new Date()` and remove this constant.
export const TODAY = new Date('2026-05-07');

export const OPPORTUNITY_ORDER: OpportunityKind[] = [
  'no-followup',
  'annual-checkup',
  'metabolic-risk',
  'cardiovascular-risk',
  'inactive',
];

export const OPPORTUNITY_DEFS: Record<OpportunityKind, OpportunityDef> = {
  'no-followup': {
    kind: 'no-followup',
    title: 'Sin seguimiento',
    description:
      'Resultados alterados sin consulta posterior agendada o atendida.',
    suggestedAction: 'Agendar consulta de seguimiento',
    severity: 'crit',
  },
  'annual-checkup': {
    kind: 'annual-checkup',
    title: 'Chequeo anual pendiente',
    description: 'Última evaluación entre 9 y 12 meses atrás.',
    suggestedAction: 'Programar control anual',
    severity: 'warn',
  },
  'metabolic-risk': {
    kind: 'metabolic-risk',
    title: 'Riesgo metabólico',
    description:
      'Glucosa, HbA1c, triglicéridos o IMC en zona de riesgo.',
    suggestedAction: 'Plan metabólico · 8 semanas',
    severity: 'warn',
  },
  'cardiovascular-risk': {
    kind: 'cardiovascular-risk',
    title: 'Riesgo cardiovascular',
    description: 'LDL, ApoB, presión o inflamación elevadas.',
    suggestedAction: 'Evaluación cardiovascular',
    severity: 'crit',
  },
  inactive: {
    kind: 'inactive',
    title: 'Pacientes inactivos',
    description: 'Sin actividad en los últimos 12 meses.',
    suggestedAction: 'Reenganche · llamada o mensaje',
    severity: 'neutral',
  },
};

export type TimeRange = '3m' | 'year' | 'all';

export const TIME_RANGE_ORDER: TimeRange[] = ['3m', 'year', 'all'];

export const TIME_RANGE_LABEL: Record<TimeRange, string> = {
  '3m': 'Últimos 3 meses',
  year: 'Año actual',
  all: 'Todos hasta hoy',
};

const RANGE_CUTOFF: Record<TimeRange, Date | null> = {
  '3m': new Date('2026-02-07'),
  year: new Date('2026-01-01'),
  all: null,
};

function daysBetween(a: Date, b: Date): number {
  return Math.floor((a.getTime() - b.getTime()) / (1000 * 60 * 60 * 24));
}

export function daysSince(iso: string | undefined): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return daysBetween(TODAY, d);
}

function inRange(p: Patient, range: TimeRange): boolean {
  const cutoff = RANGE_CUTOFF[range];
  if (!cutoff) return true;
  if (!p.lastEvaluationAt) return false;
  return new Date(p.lastEvaluationAt) >= cutoff;
}

// "annual-checkup" and "inactive" describe absence of recent activity,
// so the recency filter doesn't apply to them.
function rangeApplies(kind: OpportunityKind): boolean {
  return kind !== 'inactive' && kind !== 'annual-checkup';
}

export function patientMatchesOpportunity(
  p: Patient,
  kind: OpportunityKind
): boolean {
  const days = daysSince(p.lastEvaluationAt);
  switch (kind) {
    case 'no-followup':
      return (
        p.appointmentState === 'programado' &&
        p.status !== 'ok' &&
        days !== null &&
        days <= 90
      );
    case 'annual-checkup':
      return days !== null && days >= 270 && days <= 400;
    case 'metabolic-risk':
      return !!p.riskTags?.includes('metabolic');
    case 'cardiovascular-risk':
      return !!p.riskTags?.includes('cardiovascular');
    case 'inactive':
      return days === null || days > 365;
  }
}

const STATUS_WEIGHT: Record<Status, number> = { crit: 0, warn: 1, ok: 2 };

export function getSignals(
  patients: Patient[],
  range: TimeRange,
  kind: OpportunityKind
): PatientSignal[] {
  const scoped = rangeApplies(kind)
    ? patients.filter((p) => inRange(p, range))
    : patients;

  return scoped
    .filter((p) => patientMatchesOpportunity(p, kind))
    .map((p) => ({
      patient: p,
      kind,
      daysSinceEvaluation: daysSince(p.lastEvaluationAt),
    }))
    .sort((a, b) => {
      const ds = STATUS_WEIGHT[a.patient.status] - STATUS_WEIGHT[b.patient.status];
      if (ds !== 0) return ds;
      return (b.daysSinceEvaluation ?? Infinity) - (a.daysSinceEvaluation ?? Infinity);
    });
}

export function getSignalCounts(
  patients: Patient[],
  range: TimeRange
): Record<OpportunityKind, number> {
  return OPPORTUNITY_ORDER.reduce((acc, k) => {
    acc[k] = getSignals(patients, range, k).length;
    return acc;
  }, {} as Record<OpportunityKind, number>);
}

export function formatLastEvaluation(iso: string | undefined): string {
  const days = daysSince(iso);
  if (days === null) return 'Sin evaluación';
  if (days < 7) return days === 0 ? 'Hoy' : `Hace ${days} d`;
  if (days < 31) return `Hace ${Math.round(days / 7)} sem`;
  if (days < 365) return `Hace ${Math.round(days / 30)} m`;
  const years = days / 365;
  return `Hace ${years.toFixed(1)} años`;
}
